import { AfterViewChecked, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, DoCheck, NgZone, inject, PLATFORM_ID } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EMPTY, Subject, Subscription } from 'rxjs';
import { catchError, finalize, switchMap, takeUntil, tap, startWith, pairwise, debounceTime, filter, distinctUntilChanged } from 'rxjs/operators';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

// PrimeNG
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { InputTextModule } from 'primeng/inputtext';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { DropdownModule } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';
import { FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { DialogModule } from 'primeng/dialog';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { OverlayPanel } from 'primeng/overlaypanel';
import { ListboxModule } from 'primeng/listbox';

// services
import { CampaignService } from '../../../services/campaign.service';
import { AgentResponseReceptionService } from '../../../services/agent-response-reception.service';
import { MessageManagementService } from '../../../services/message-management.service';
import { WorkspaceService } from '../../../services/workspace.service';
import { CustomerMessageService } from '../../../services/customer-message.service';
import { AgentDisconnectedService } from '../../../services/agent-disconnected.service';
import { SharedDataService } from '../../../services/shared-data.service';
import { FileAdministratorService } from '../../../services/file-administrator.service';
import { CustomerConversationService } from '../../../services/customer-conversation.service';
import { parseMediaTypeToContentType } from '../../../utils/file';


// Models
import { AgentConfig, Campaign, ConversationConfig, TimeSettings, TimeSettingsConfig, QuestionAnswerItem } from '../../../models/campaign';
import { ChatStatus, ContentMessageType, Conversation, FileMessage, MessageInterface, ConversationStatus } from '../../../models/chat';
import { Agent } from '../../../models/users';

// Components
import DOMPurify from 'dompurify';
import { VirtualScrollerComponent, VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
import { DocumentMessageComponent } from '../document-message/document-message.component';
import { PanelEmojiesComponent } from '../../panel-emojies/panel-emojies.component';
import { AgentPanelMetricsComponent } from '../agent-panel-metrics/agent-panel-metrics.component';
import { ChatTitleBarComponent } from '../chat-title-bar/chat-title-bar.component';

// websocket
import { ConnectionState, ConnectionStatus, WebSocketManagerService } from '../../../connectivity';

interface CloseReason {
  label: string;
  value: string | null;
}

@Component({
  selector: 'app-chat-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    TextareaModule,
    InputTextModule,
    AvatarModule,
    AvatarGroupModule,
    DropdownModule,
    TooltipModule,
    VirtualScrollerModule,
    PanelEmojiesComponent,
    AgentPanelMetricsComponent,
    ChatTitleBarComponent,
    DocumentMessageComponent,
    FileUploadModule,
    DialogModule,
    OverlayPanelModule,
    ListboxModule,
    TranslateModule,
  ],
  templateUrl: './chat-panel.component.html',
  styleUrls: ['./chat-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [
    CUSTOM_ELEMENTS_SCHEMA
  ],
})
export class ChatPanelComponent implements OnInit, OnChanges, AfterViewChecked, OnDestroy, DoCheck {

  @Input() messagesIncoming: MessageInterface[] = [];
  @Input() selectedConversation: Conversation | null = null;
  @Input() user: Agent | null = null; // Add user input to determine role

  private readonly destroy$ = new Subject<void>();
  private conversationSubscription!: Subscription;
  private readonly type_agent: ChatStatus = 'agent';
  public workspaceId: string = '';
  public platformId: number = 0;

  // Ticket input for conversations when campaign enables ticket field
  ticketInput: string = '';
  ticketSaved: boolean = false;

  // Loading states
  isLoadingConversation = false;
  isLoadingCampaign = false;
  conversationError: string | null = null;
  campaignError: string | null = null;

  @ViewChild('messageArea') messageAreaRef!: ElementRef;
  private _chatListRef?: ElementRef<HTMLDivElement>;

  @ViewChild('chatListContainer', { read: ElementRef })
  set chatListContainer(ref: ElementRef<HTMLDivElement> | undefined) {
    this._chatListRef = ref;
    if (ref) {

      requestAnimationFrame(() => this.scrollToBottom());
    }
  }
  @ViewChild('scroll') virtualScroller?: VirtualScrollerComponent;

  @ViewChild('messagesPanel') messagesPanel!: OverlayPanel;
  @ViewChild('questionsPanel') questionsPanel!: OverlayPanel;

  private readonly zone = inject(NgZone);

  private isScrolling: boolean = false;

  // View state properties
  isInfoPanelVisible: boolean = false;
  isPanelAnimating: boolean = false;

  // Panel transition control
  panelTransitionClass: string = '';

  closeReasons: CloseReason[] = [];

  agent: Agent | null = null; // Current agent information
  campaign: Campaign | null = null;
  // Tracks the campaignId whose data is already loaded so we don't refetch (and
  // so a re-emission of conversation$ can't cancel an in-flight campaign GET).
  private loadedCampaignId: string | null = null;
  chatInputMessage: string = '';
  chatMessages: MessageInterface[] = [];
  AGENT_ROLE: ChatStatus = 'agent';
  USER_ROLE: ChatStatus = 'user';

  name = 'Chat Angular';
  message = '';

  // UI labels (getters so they react to language changes and avoid using
  // `this.translate` before constructor initialization).
  get placeholderText(): string { return this.translate.instant('agent.chat.messagePlaceholder'); }
  get emojiPickerTitle(): string { return this.translate.instant('agent.chat.emojiPickerTitle'); }

  // Responsive size settings
  maxEmojiPickerHeight = 350;
  messageAreaHeight = 0;

  // Appearance
  isDarkMode = false;

  // Translation feature
  isTranslationEnabled: boolean = false;

  currentConversation!: string;

  // Campaign selection functionality (replaces agent transfer)
  availableCampaigns: Campaign[] = [];
  selectedCampaign: Campaign | null = null;
  isLoadingCampaigns = false;
  showCampaignDialog = false;

  // Expose ContentMessageType enum to the template
  ContentMessageType = ContentMessageType;

  // Panel metadata formatted for display with improved null-safety
  get campaignMetadata(): { label: string, value: string }[] {
    if (!this.campaign) return [];

    const naValue = this.translate.instant('agent.chat.na');
    return [
      { label: this.translate.instant('agent.chat.meta.name'), value: this.campaign?.name || naValue },
      { label: this.translate.instant('agent.chat.meta.type'), value: this.campaign?.type || naValue },
      { label: this.translate.instant('agent.chat.meta.state'), value: this.campaign?.state || naValue },
      { label: this.translate.instant('agent.chat.meta.priority'), value: this.campaign?.priority?.toString() || naValue },
      { label: this.translate.instant('agent.chat.meta.ratio'), value: this.campaign?.ratio || naValue },
      { label: this.translate.instant('agent.chat.meta.currentAction'), value: this.campaign?.current_action || naValue },
      { label: this.translate.instant('agent.chat.meta.closingReasons'), value: this.campaign?.closing_reason?.join(', ') || naValue }
    ];
  }

  get conversationMetadata(): { label: string, value: string }[] {
    if (!this.selectedConversation) return [];

    const naValue = this.translate.instant('agent.chat.na');
    return [
      { label: this.translate.instant('agent.chat.meta.id'), value: this.selectedConversation?.conversationId || naValue },
      { label: this.translate.instant('agent.chat.meta.clientId'), value: this.selectedConversation?.clientId || naValue },
      { label: this.translate.instant('agent.chat.meta.campaignId'), value: this.selectedConversation?.campaignId || naValue },
      { label: this.translate.instant('agent.chat.meta.campaign'), value: this.campaign?.campaign_name || naValue },
      { label: this.translate.instant('agent.chat.meta.platform'), value: this.selectedConversation?.platform || naValue },
      { label: this.translate.instant('agent.chat.meta.state'), value: this.selectedConversation?.status || naValue },
      {
        label: this.translate.instant('agent.chat.meta.startDate'), value: this.selectedConversation?.startDate ?
          new Date(this.selectedConversation.startDate).toLocaleString() : naValue
      },
      { label: this.translate.instant('agent.chat.meta.agent'), value: this.selectedConversation?.agentId || naValue }
    ];
  }

  messageContentRender(message: MessageInterface): string {
    // User validation and translation logic
    const isUser = message.type === this.USER_ROLE;
    const isTranslationEnabled = this.selectedConversation?.metadata?.translationConfig?.enable;

    if (!isTranslationEnabled || isUser) {
      return message.content;
    }

    return message.contentTranslated as string;
  }

  public isContextMessage(message: MessageInterface): boolean {
    return !!message.content?.trim().toLowerCase().startsWith('contexto:');
  }

  copyToClipboard(text: string) {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  }

  public parseContextContent(message: MessageInterface): Array<{ label: string; value: string }> {
    const withoutHeader = message.content!
      .replace(/Contexto:\s*<br\s*\/?>/i, '')
      .trim();
    return withoutHeader
      .split(/<br\s*\/?>/i)
      .map(line => line.trim())
      .filter(line => line)
      .map(line => {
        const [key, ...rest] = line.split(':');
        return { label: key.trim(), value: rest.join(':').trim() };
      });
  }

  // Check if campaign selection is available
  get canSelectCampaign(): boolean {
    // Valida que workspaceId sea string no vacío
    const validWorkspace = typeof this.workspaceId === 'string' && this.workspaceId.trim() !== '';
    // Valida que platformId sea número mayor a 0
    const validPlatform = typeof this.platformId === 'number' && this.platformId > 0;
    // Valida que la conversación esté activa
    const isActiveConversation = this.selectedConversation?.status === ConversationStatus.Active;
    const canTransferBetweenAgent =
      !!this.campaign?.agent_config?.enable &&
      !!this.campaign?.agent_config?.transfer_between_agent;

    return validWorkspace && validPlatform && isActiveConversation && canTransferBetweenAgent;
  }
  // Check if default messages are enabled in campaign
  get isDefaultMessagesEnabled(): boolean {
    return !!(this.campaign?.default_messages_config?.enable &&
      this.campaign?.default_messages_config?.messages?.length);
  }

  // Check if default questions are enabled in campaign
  get isDefaultQuestionsEnabled(): boolean {
    return !!(this.campaign?.default_questions_answers_config?.enable &&
      this.campaign?.default_questions_answers_config?.questions_answers?.length);
  }

  // Get default messages from campaign
  get defaultMessages(): string[] {
    return this.campaign?.default_messages_config?.messages || [];
  }

  // Get default questions from campaign
  get defaultQuestions(): QuestionAnswerItem[] {
    return this.campaign?.default_questions_answers_config?.questions_answers || [];
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    // Send message on Enter without shift key
    if (event.key === 'Enter' && !event.shiftKey && document.activeElement?.classList.contains('chat-input')) {
      event.preventDefault();
      this.send();
      return;
    }

    // Allow new lines with Shift+Enter
    if (event.key === 'Enter' && event.shiftKey && document.activeElement?.classList.contains('chat-input')) {
      return; // Let the default behavior happen (new line)
    }
  }

  constructor(
    private agentResponseReceptionService: AgentResponseReceptionService,
    private customerMessageService: CustomerMessageService,
    private customerConversationService: CustomerConversationService,
    private cdr: ChangeDetectorRef,
    private agentDisconnectedService: AgentDisconnectedService,
    private campaignService: CampaignService,
    private sharedDataService: SharedDataService,
    private sanitizer: DomSanitizer,
    private elementRef: ElementRef,
    private fileAdministratorService: FileAdministratorService,
    private messageManagementService: MessageManagementService,
    private workspaceService: WorkspaceService,
    private webSocketManagerService: WebSocketManagerService,
    private translate: TranslateService,
  ) {
  }

  saveTicket(): void {
    if (!this.selectedConversation?.conversationId) {
      return;
    }
    const convId = this.selectedConversation.conversationId;
    const ticket = (this.ticketInput || '').trim();
    if (!ticket) {
      return;
    }

    this.customerConversationService.patchConversationTicket(convId, ticket)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.ticketSaved = true;
          // If backend returned updated conversation, use it; otherwise fallback to local update
          if (res && (res as Conversation).conversationId) {
            this.selectedConversation = res as Conversation;
            try {
              this.customerConversationService.setConversation(res as Conversation);
            } catch (e) {
              // best-effort update
            }
          } else if (this.selectedConversation) {
            const updated = { ...this.selectedConversation, ticketId: ticket } as Conversation;
            this.selectedConversation = updated;
            try {
              this.customerConversationService.setConversation(updated);
            } catch (e) {
              // best-effort update
            }
          }
          this.cdr.markForCheck();
        },
        error: (err) => {
        }
      });
  }

  ngOnInit(): void {

    // initialize agent
    this.initializeAgent();

    // setup conversation subscription
    this.setupConversationSubscription();

    // setup connection websocket monitoring
    this.initializeWebSocketMonitoring();

    const workspace = JSON.parse(this.workspaceService.getCurrentWorkspace());
    this.workspaceId = workspace?.id || '';
    // platformId must be a positive number for the transfer/campaign-selection
    // getters. The persisted workspace exposes it as a string ("3"), and
    // companyPlatformId is a UUID, so coerce the numeric platform id here.
    this.platformId = Number(workspace?.platformId ?? workspace?.platformNumericId) || 0;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedConversation']) {
      if (!this.selectedConversation) {
        this.chatMessages = [];
        this.selectedConversation = null;
      } else {

        this.handleConversationChange(this.selectedConversation);
      }


    }
  }

  @HostListener('window:online')
  onOnline(): void {
    //console.log('🌐 Navegador en línea');
    this.getMessages(); // Fetch messages on reconnection
  }

  @HostListener('window:offline')
  onOffline(): void {
    //console.log('📴 Navegador sin conexión');
    // No acción inmediata, reconexión se maneja al restaurar conexión
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {

  }

  send(): void {
    if (this.chatInputMessage.trim() === '') return;
    if (!this.selectedConversation?.conversationId) {
      console.error('No conversation selected.');
      return;
    }

    const maxLength = 500;
    const message = this.chatInputMessage.trim();

    if (message.length <= maxLength) {
      this.sendSingleMessage(message);
    } else {
      this.sendMultipleMessage(message, maxLength);
    }

    // Clear input immediately for better UX
    this.chatInputMessage = '';
    this.cdr.markForCheck();
  }

  private sendSingleMessage(messageContent: string): void {
    if (!this.selectedConversation?.conversationId) {
      console.error('No conversation selected.');
      return;
    }

    const messageToSend: MessageInterface = {
      content: messageContent,
      messageType: ContentMessageType.Text,
      conversationId: this.selectedConversation.conversationId,
      type: this.type_agent
    };

    this.agentResponseReceptionService.submitMessage(messageToSend).subscribe({
      next: (response: MessageInterface) => {
        this.isScrolling = true; // Enable scrolling after sending a message
        this.chatMessages = [...this.chatMessages, response];
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('Error sending message:', error);
      },
    });
  }

  private sendMultipleMessage(message: string, maxLength: number): void {
    const chunks = this.splitMessage(message, maxLength);

    chunks.forEach((chunk, index) => {
      setTimeout(() => {
        this.sendSingleMessage(chunk);
      }, index * 100);
    })
  }

  private splitMessage(message: string, maxLength: number) {
    const chunks: string[] = [];

    const words = message.split(' ');
    let currentChunk = '';

    for (const word of words){
      if ((currentChunk + ' ' + word).length <= maxLength){
        currentChunk += (currentChunk ?  ' ' : '') + word;
      } else {
        if (currentChunk) chunks.push(currentChunk);
        currentChunk =  word;
      }
    }

    if (currentChunk) chunks.push(currentChunk);
    return chunks;
  }



  // --- Cleanup on destroy ---

  ngOnDestroy(): void {
    console.log('🧹 ChatPanel: Iniciando limpieza de recursos...');

    // Destroy RxJS subjects to complete all streams
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up other subscriptions
    if (this.lastMessageSubscription && !this.lastMessageSubscription.closed) {
      this.lastMessageSubscription.unsubscribe();
      console.log('✅ Last message subscription unsubscribed');
    }

    if (this.messageUpdateSubscription && !this.messageUpdateSubscription.closed) {
      this.messageUpdateSubscription.unsubscribe();
      console.log('✅ Message update subscription unsubscribed');
    }

    // Clean up conversation subscription
    if (this.conversationSubscription && !this.conversationSubscription.closed) {
      this.conversationSubscription.unsubscribe();
      console.log('✅ Conversation subscription unsubscribed');
    }

    // Clear any remaining references
    this.selectedConversation = null;
    this.chatMessages = [];
    this.agent = null;
    this.campaign = null;

    console.log('✅ ChatPanel: Limpieza de recursos completada');
  }

  private initializeAgent(): void {
    this.agent = this.sharedDataService.getAgent();

    if (!this.agent) {
      console.error('No agent found.');
      return;
    }
  }

  private setupConversationSubscription(): void {
    this.isLoadingConversation = true;
    this.conversationError = null;

    this.customerConversationService.conversation$
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.isLoadingConversation = true;
          this.conversationError = null;
          this.cdr.markForCheck();
        }),
        switchMap((conversation: Conversation | null) => {
          // Clear previous messages and reset state

          if (this.selectedConversation?.conversationId != conversation?.conversationId) {

            this.messageManagementService.clearMessages(); // Clear previous messages
            this.chatMessages = []; // Reset chat messages array
          }

          if (!conversation || !conversation.conversationId) {
            this.loadedCampaignId = null;
            this.handleNoConversationSelected();
            return EMPTY;
          }
          // console.log('📥 Nueva conversación recibida:', conversation);

          this.currentConversation = conversation.conversationId;
          this.handleConversationChange(conversation);
          // Load the campaign in its own subscription so later emissions of
          // conversation$ don't cancel the in-flight GET. A cancelled request
          // left this.campaign null and hid the transfer button. Only reload
          // when the campaignId actually changes.
          if (conversation.campaignId && conversation.campaignId !== this.loadedCampaignId) {
            this.loadedCampaignId = conversation.campaignId;
            this.loadCampaignForConversation(conversation.campaignId).subscribe();
          }
          return EMPTY;
        }),
        catchError((error) => {
          console.error('Error in conversation subscription:', error);
          this.conversationError = 'Error loading conversation. Please try again.';
          this.isLoadingConversation = false;
          this.cdr.markForCheck();
          return EMPTY;
        }),
        finalize(() => {
          this.isLoadingConversation = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }


  /*
    WebSocket connection monitoring
  */
  private initializeWebSocketMonitoring(): void {

    console.log('🔌 Initializing WebSocket connection monitoring Chat Panel Component');

    // Monitor connection state changes with recovery detection
    this.webSocketManagerService.state$.pipe(
      takeUntil(this.destroy$),
      startWith(null),
      pairwise(),
      debounceTime(500) // Debounce rapid state changes
    ).subscribe(([prev, curr]) => {

      if (!curr) { return; }

      console.log('🔌 WebSocket state (ChatPanel):', curr);

      this.handleConnectionStateChange(prev, curr);

    });

    // Additional connection establishment monitoring
    this.webSocketManagerService.state$.pipe(
      takeUntil(this.destroy$),
      filter(state => state.status === ConnectionStatus.CONNECTED),
      distinctUntilChanged((prev, curr) => prev.lastConnected === curr.lastConnected)
    ).subscribe(state => {

      // console.log('🔌 WebSocket connection established (ChatPanel):', state);
    });

    // CRITICAL FIX: Listen for circuit breaker recovery events
    // This detects successful recovery from emergency/circuit breaker states
    this.webSocketManagerService.events$.pipe(
      takeUntil(this.destroy$),
      filter(event => {
        // Look for circuit breaker closed events
        if (event.type === 'message' && event.data) {
          // Handle different possible data structures
          if (typeof event.data === 'object') {
            return event.data.type === 'circuit_breaker_closed' ||
              event.data.event === 'circuit_breaker_closed' ||
              (event.data.state === 'CLOSED' && event.data.state !== undefined);
          }
          if (typeof event.data === 'string') {
            return event.data === 'circuit_breaker_closed';
          }
        }
        return false;
      })
    ).subscribe(event => {
      const currentTime = new Date().toISOString();

      console.log('✅ CHAT PANEL: Circuit breaker closed - successful recovery detected:', {
        timestamp: currentTime,
        conversationId: this.selectedConversation?.conversationId,
        eventData: event.data,
        reason: 'circuit_breaker_recovery_confirmed'
      });

      console.log('🔄 CHAT PANEL: Reloading messages after successful recovery:', {
        timestamp: currentTime,
        conversationId: this.selectedConversation?.conversationId,
        trigger: 'circuit_breaker_closed_event'
      });

      // Simple recovery action: reload messages when circuit breaker confirms recovery
      if (this.selectedConversation?.conversationId) {
        this.getMessages();
      }
    });

    // ADDITIONAL FIX: Listen for specific log-based recovery patterns
    // Monitor for events that indicate successful ping after HALF_OPEN transition
    this.webSocketManagerService.events$.pipe(
      takeUntil(this.destroy$),
      filter(event => event.type === 'status' || event.type === 'message'),
      filter(event => {
        if (event.data && typeof event.data === 'object') {
          // Look for successful connection events after recovery attempts
          return event.data.wasReconnection ||
            event.data.recoverySuccess ||
            (event.data.message && event.data.message.includes('PING: Response received')) ||
            (event.data.url && event.data.connectionTime !== undefined && event.data.attempt > 0);
        }
        return false;
      }),
      debounceTime(1000) // Debounce to prevent multiple rapid calls
    ).subscribe(event => {
      const currentTime = new Date().toISOString();

      console.log('✅ CHAT PANEL: Recovery success detected via status event:', {
        timestamp: currentTime,
        conversationId: this.selectedConversation?.conversationId,
        eventData: event.data,
        reason: 'status_based_recovery_detection'
      });

      console.log('🔄 CHAT PANEL: Reloading messages after status-based recovery:', {
        timestamp: currentTime,
        conversationId: this.selectedConversation?.conversationId,
        trigger: 'status_recovery_event'
      });

      // Simple recovery action: reload messages on confirmed recovery
      if (this.selectedConversation?.conversationId) {
        this.getMessages();
      }
    });

  }

  /**
   * Handle connection state changes and detect recovery events
   * Following app.component.ts patterns for recovery detection
   */
  private handleConnectionStateChange(previous: ConnectionState | null, current: ConnectionState) {

    if (!previous || !current) {
      return;
    }

    const currentTime = new Date().toISOString();
    const previousConnected = previous.status === ConnectionStatus.CONNECTED;
    const currentConnected = current.status === ConnectionStatus.CONNECTED;

    // Detect connection recovery (not initial connection)
    if (!previousConnected && currentConnected) {
      const wasReconnection = current.reconnectAttempts > 0;

      console.log('✅ CHAT PANEL: CONEXIÓN WEBSOCKET RECUPERADA:', {
        timestamp: currentTime,
        conversationId: this.selectedConversation?.conversationId,
        previousStatus: previous.status,
        currentStatus: current.status,
        networkQuality: current.quality,
        reconnectAttempts: current.reconnectAttempts,
        wasReconnection: wasReconnection
      });

      // Simple recovery listener: call getMessages() when connection is successfully recovered
      if (wasReconnection && this.selectedConversation?.conversationId) {
        console.log('🔄 CHAT PANEL: Reloading messages after WebSocket recovery:', {
          timestamp: currentTime,
          conversationId: this.selectedConversation.conversationId,
          reason: `websocket_recovery_attempt_${current.reconnectAttempts}`
        });

        this.getMessages();
      } else if (!wasReconnection) {
        console.log('ℹ️ CHAT PANEL: Initial connection established (no reload needed):', {
          timestamp: currentTime,
          conversationId: this.selectedConversation?.conversationId
        });
      }
    }

    // Log connection loss for debugging
    if (previousConnected && !currentConnected) {
      console.warn('🔥 CHAT PANEL: CONEXIÓN WEBSOCKET PERDIDA:', {
        timestamp: currentTime,
        conversationId: this.selectedConversation?.conversationId,
        previousStatus: previous.status,
        currentStatus: current.status,
        networkQuality: current.quality,
        reconnectAttempts: current.reconnectAttempts
      });
    }
  }

  private handleNoConversationSelected(): void {
    // console.warn('No conversation selected.');
    this.selectedConversation = null;
    this.chatMessages = [];
    this.campaign = null;
    this.closeReasons = [];
    this.isInfoPanelVisible = false;
    this.panelTransitionClass = '';
    this.isLoadingConversation = false;
    this.cdr.markForCheck();
  }

  private lastMessageSubscription!: Subscription;
  lastMessage: MessageInterface | null = null;

  private messageUpdateSubscription!: Subscription;

  private handleConversationChange(conversation: Conversation): void {
    if (!conversation || !conversation.conversationId) {
      this.handleNoConversationSelected();
      return;
    }

    // Clear previous messages and reset state
    this.selectedConversation = conversation;
    this.chatMessages = []; // Clear messages from previous conversation
    this.lastMessage = null; // Reset last message
    this.isInfoPanelVisible = false;
    this.panelTransitionClass = '';

    // Load ticket input from conversation if present
    this.ticketInput = (conversation as any).ticketId || '';
    this.ticketSaved = !!this.ticketInput;

    // Unsubscribe from previous subscriptions to avoid memory leaks
    this.lastMessageSubscription?.unsubscribe();
    this.conversationSubscription?.unsubscribe();
    this.messageUpdateSubscription?.unsubscribe();

    // Fetch the last message for the selected conversation
    this.lastMessageSubscription = this.messageManagementService
      .getLastMessageByConversationId(conversation.conversationId)
      .subscribe({
        next: (message: MessageInterface | undefined) => {
          this.lastMessage = message || null;
          this.isScrolling = true;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error fetching last message:', error);
          this.lastMessage = null; // Show "Sin mensajes" if an error occurs
          this.cdr.markForCheck();
        },
      });

    // Subscribe to new messages for the selected conversation
    this.messageUpdateSubscription = this.messageManagementService
      .onMessageConversationIdReceived(conversation.conversationId)
      .subscribe({
        next: (message: MessageInterface | undefined) => {
          if (message) {

            // Convert messageType from string to enum if necessary to ensure correct rendering
            let messageType = message.messageType;
            if (typeof messageType === 'string') {
              messageType = this.convertMessageTypeStringToEnum(messageType);
            }
            const processedMessage: MessageInterface = {
              ...message,
              messageType: messageType
            };

            this.chatMessages = [...this.chatMessages, processedMessage];
            this.cdr.markForCheck();
          }
        },
        error: (error: any) => {
          console.error('Error receiving new messages:', error);
        },
      });

    // Fetch all messages for the conversation
    this.getMessages();
    this.loadConversationVariables();

    // Load available campaigns for selection functionality
    this.loadCampaignsByWorkspace();

    this.cdr.markForCheck();
  }

  getDisplayContent(message: MessageInterface): string {
    return this.isTranslationEnabled && this.hasTranslation(message)
      ? message.contentTranslated ?? ''
      : message.content ?? '';
  }


  private loadCampaignForConversation(campaignId: string) {
    if (!campaignId) {
      // console.warn('No campaign ID provided');
      this.campaign = null;
      this.closeReasons = [];
      // Reset configuration data when no campaign
      this.agentConfigData = null;
      this.conversationConfigData = null;
      this.timeSettingsData = null;
      return EMPTY;
    }

    this.isLoadingCampaign = true;
    this.campaignError = null;

    return this.campaignService.getCampaignById(campaignId)
      .pipe(
        takeUntil(this.destroy$),
        tap((campaign: Campaign) => {
          this.campaign = campaign;
          this.closeReasons = this.buildCloseReasons(campaign.closing_reason);
          // Load configuration data for the three-column config panel
          this.loadConfigurationData();
          this.isLoadingCampaign = false;
          this.cdr.markForCheck();
        }),
        catchError((error) => {
          console.error('Error fetching campaign:', error);
          this.campaignError = 'Error loading campaign data. Please try again.';
          this.campaign = null;
          this.loadedCampaignId = null; // allow a retry on the next emission
          this.closeReasons = [];
          // Reset configuration data on error
          this.agentConfigData = null;
          this.conversationConfigData = null;
          this.timeSettingsData = null;
          this.isLoadingCampaign = false;
          this.cdr.markForCheck();
          return EMPTY;
        })
      );
  }

  private buildCloseReasons(reasons: string[]): CloseReason[] {
    if (!Array.isArray(reasons)) {
      // console.warn('Invalid closing reasons format:', reasons);
      return [];
    }

    return reasons
      .filter(reason => reason && reason.trim()) // Filter out empty/null reasons
      .map((reason: string) => ({
        label: reason.trim(),
        value: reason.trim(),
      }));
  }

  /**
   * Improves the redaction of messages in the current conversation.
   */
  improveRedaction(): void {
    if (!this.selectedConversation?.conversationId) {
      console.error('No conversation selected for redaction improvement.');
      return;
    }
    // TODO: Implement redaction improvement logic
  }

  /**
   * Handles the file upload event from the p-fileUpload component.
   * @param event The FileUploadHandlerEvent containing the selected files.
   */
  onFileUpload(event: FileUploadHandlerEvent): void {

    const file: File = event.files[0]; // Get the first selected file
    if (!file) {
      console.warn('No file selected.');
      return;
    }

    if (!this.selectedConversation?.conversationId) {
      console.error('Cannot upload file: No conversation selected.');
      return;
    }

    if (!this.selectedConversation?.conversationId) {
      console.error('No conversation selected.');
      return;
    }

    this.fileAdministratorService.uploadFile(file).subscribe({
      next: (fileMessage: FileMessage) => {
        //console.log('File uploaded successfully:', fileMessage);

        let message_type = parseMediaTypeToContentType(file.type);
        //console.log('Parsed message type:', message_type);


        const messageToSend: MessageInterface = {
          content: fileMessage.url,
          messageType: parseMediaTypeToContentType(file.type),
          conversationId: this.selectedConversation?.conversationId as string,
          type: this.type_agent,
        };
        //console.log('Message to send:', messageToSend);

        // Clear input immediately for better UX
        const pendingContent = this.chatInputMessage;
        this.chatInputMessage = '';
        this.cdr.markForCheck();

        this.agentResponseReceptionService.submitMessage(messageToSend).subscribe({
          next: (response: MessageInterface) => {
            this.isScrolling = true; // Case 1: Enable scrolling after sending a message
            this.chatMessages = [...this.chatMessages, response];
            this.cdr.markForCheck();
          },
          error: (error: any) => {
            console.error('Error sending message:', error);
            // Restore the message if sending failed
            this.chatInputMessage = pendingContent;
            this.cdr.markForCheck();
          },
        });

      },
      error: (error: any) => {
        console.error('Error uploading file:', error);
      },
    });

    this.cdr.markForCheck(); // Update view if needed
  }

  /**
   * Toggles between chat and information panel views with animation
   */
  toggleInfoPanel(): void {
    if (this.isPanelAnimating) return;

    this.isPanelAnimating = true;

    if (this.isInfoPanelVisible) {
      // Transition from info panel to chat panel
      this.panelTransitionClass = 'axede-fade-out';
      this.isScrolling = true; // Enable scrolling after hiding info panel

      setTimeout(() => {
        this.isInfoPanelVisible = false;
        this.panelTransitionClass = 'axede-fade-in';
        this.isPanelAnimating = false;
        this.cdr.markForCheck();
      }, 300); // Match with animation duration
    } else {
      // Transition from chat panel to info panel
      this.panelTransitionClass = 'axede-fade-out';

      setTimeout(() => {
        this.isInfoPanelVisible = true;
        this.panelTransitionClass = 'axede-fade-in';
        this.isPanelAnimating = false;
        this.cdr.markForCheck();
      }, 300); // Match with animation duration
    }

    this.cdr.markForCheck();
  }

  addEmoji(emoji: string) {
    if (!emoji) return;

    const inputElement = this.elementRef.nativeElement.querySelector('.chat-input');

    const startPos = inputElement.selectionStart || this.chatInputMessage.length;
    const endPos = inputElement.selectionEnd || this.chatInputMessage.length;

    this.chatInputMessage =
      this.chatInputMessage.substring(0, startPos) +
      emoji +
      this.chatInputMessage.substring(endPos);

    setTimeout(() => {
      inputElement.focus();

      const newCursorPos = startPos + emoji.length;
      inputElement.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }

  // Default Messages functionality
  selectDefaultMessage(message: string): void {
    this.chatInputMessage = message;
    if (this.messagesPanel) {
      this.messagesPanel.hide();
    }
    // Focus on textarea
    const inputElement = this.elementRef.nativeElement.querySelector('.chat-input');
    if (inputElement) {
      inputElement.focus();
    }
  }

  // Default Questions functionality
  selectDefaultQuestion(questionAnswer: QuestionAnswerItem): void {
    this.chatInputMessage = questionAnswer.answer;
    if (this.questionsPanel) {
      this.questionsPanel.hide();
    }
    // Focus on textarea
    const inputElement = this.elementRef.nativeElement.querySelector('.chat-input');
    if (inputElement) {
      inputElement.focus();
    }
  }


  ngAfterViewChecked(): void {

  }

  // Before Angular updates the view, save the scroll position if not auto-scrolling
  ngDoCheck(): void {

  }

  private scrollToBottom(): void {

    if (
      !this.selectedConversation ||
      !this._chatListRef?.nativeElement ||
      !this._chatListRef.nativeElement.scrollHeight
    ) {
      return;
    }

    if (!this.isScrolling) {
      // If already scrolling, skip to avoid conflicts
      return;
    }
    this.isScrolling = false;

    const el = this._chatListRef.nativeElement;
    this.zone.runOutsideAngular(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
  }

  trackByMessageId(index: number, item: MessageInterface): string {
    return item.messageId || `${item.timestamp}-${index}`;
  }


  closeConversation(closureReason: string): void {
    const conversationId = this.selectedConversation?.conversationId;

    if (!conversationId) {
      console.error('No conversation selected.');
      return;
    }

    if (!closureReason) {
      console.error('No closure reason provided.');
      return;
    }


    this.agentDisconnectedService
      .closeConversation(conversationId, closureReason)
      .subscribe({
        next: (conversation: Conversation) => {
          this.customerConversationService.resetConversation();
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error closing conversation:', error);
        },
      });
    ;
  }

  getMessages(): void {
    if (!this.selectedConversation || !this.selectedConversation.conversationId) {
      console.error('No conversation selected.');
      return;
    }

    this.customerMessageService.getMessagesByConversationId(
      this.selectedConversation.conversationId
    ).subscribe({
      next: (messages: MessageInterface[]) => {

        // Directly assign fetched messages after converting string types if necessary

        this.chatMessages = messages.map(message => {
          let messageType = message.messageType;
          if (typeof messageType === 'string') {
            messageType = this.convertMessageTypeStringToEnum(messageType);
          }
          return {
            ...message,
            messageType: messageType // Ensure messageType is the enum value
          };
        });
        this.cdr.markForCheck();
        this.isScrolling = true; // Enable scrolling after fetching last message
      },
      error: (error: any) => {
        console.error('Error fetching messages:', error);
      },
    });
  }

  /**
   * Toggles the display of translated messages
   */
  toggleTranslation(): void {
    this.isTranslationEnabled = !this.isTranslationEnabled;
    this.cdr.markForCheck();
  }

  /**
   * Determines if a message should be rendered as plain text or as a media component
   * Update signature to accept Message
   */
  isTextMessage(message: MessageInterface): boolean {
    const type = message.messageType;

    // Handle undefined, null, or the numeric enum value for Text
    if (type === undefined || type === null || type === ContentMessageType.Text) {
      return true;
    }
    // Handle the case where the type might be the string "text" (already converted)
    // This check might become redundant if conversion always happens before this method is called
    if (typeof type === 'string' && type === 'text') {
      return true;
    }

    // Otherwise, it's not considered a text message for rendering purposes
    return false;
  }

  /**
   * Check if a message has translated content
   * Update signature to accept Message
   */
  hasTranslation(message: MessageInterface): boolean {
    return !!message.contentTranslated && message.contentTranslated.trim() !== '';
  }

  /**
   * Gets the appropriate date for display from message
   * Update signature to accept Message
   */
  getMessageDate(message: MessageInterface): Date | null {
    return message.datePending || message.timestamp || null;
  }

  private isNearBottom(element: HTMLElement): boolean {
    return element.scrollHeight - element.scrollTop - element.clientHeight < 100;
  }

  // Helper method to detect URLs in messages that might be media
  detectMediaType(content: string): ContentMessageType {
    if (!content) return ContentMessageType.Text;

    // Simple URL detection
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    if (!urlRegex.test(content)) return ContentMessageType.Text;

    // Check common file extensions
    const lowerContent = content.toLowerCase();

    // Image types
    if (/\.(jpeg|jpg|gif|png|webp|svg|avif)(\?.*)?$/.test(lowerContent)) {
      return ContentMessageType.Image;
    }

    // Video types
    if (/\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv)(\?.*)?$/.test(lowerContent)) {
      return ContentMessageType.Video;
    }

    // Audio types
    if (/\.(mp3|wav|ogg|m4a|flac|aac)(\?.*)?$/.test(lowerContent)) {
      return ContentMessageType.Audio;
    }

    // Document types
    if (/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|csv|zip|rar)(\?.*)?$/.test(lowerContent)) {
      return ContentMessageType.Document;
    }

    // Default for URLs that don't match known media types
    return ContentMessageType.Unsupported;
  }

  sanitizeMessage(message: string): SafeHtml {
    // Make sure message is not undefined or null before using it
    const safeMessage = message || '';
    return this.sanitizer.bypassSecurityTrustHtml(DOMPurify.sanitize(safeMessage));
  }

  // Helper method to convert string message types to enum values
  private convertMessageTypeStringToEnum(type: string): ContentMessageType {
    type = type.toLowerCase();

    switch (type) {
      case 'text':
        return ContentMessageType.Text;
      case 'image':
        return ContentMessageType.Image;
      case 'video':
        return ContentMessageType.Video;
      case 'audio':
        return ContentMessageType.Audio;
      case 'document':
        return ContentMessageType.Document;
      default:
        return ContentMessageType.Unsupported;
    }
  }

  /**
   * Loads conversation variables from the selected conversation
   */
  private loadConversationVariables(): void {
    // No longer need to initialize form values since it's display-only
    this.cdr.markForCheck();
  }

  /**
   * Checks if the conversation has custom variables
   */
  get hasCustomVariables(): boolean {
    return !!(this.selectedConversation?.customVariables &&
      Object.keys(this.selectedConversation.customVariables).length > 0);
  }

  /**
   * Gets the custom variables as an array of key-value pairs for template iteration
   */
  get customVariablesArray(): Array<{ name: string, value: string }> {
    if (!this.selectedConversation?.customVariables) return [];

    return Object.entries(this.selectedConversation.customVariables)
      .filter(([name, value]) => name != null) // Filter out null/undefined keys
      .map(([name, value]) => ({
        name,
        value: value || '' // Ensure value is never null/undefined
      }));
  }

  // Configuration panel data
  agentConfigData: AgentConfig | null = null;
  conversationConfigData: ConversationConfig | null = null;
  timeSettingsData: TimeSettingsConfig | null = null;

  /**
   * Formats TimeSettings object into readable string format
   */
  formatTimeSettings(timeSettings: TimeSettings): string {
    if (!timeSettings) return this.translate.instant('agent.chat.notSpecified');

    const { hours, minutes, seconds } = timeSettings;
    const formatNumber = (num: number): string => num.toString().padStart(2, '0');

    return `${formatNumber(hours || 0)}:${formatNumber(minutes || 0)}:${formatNumber(seconds || 0)}`;
  }

  /**
   * Loads configuration data for the three-column config panel
   */
  private loadConfigurationData(): void {
    if (!this.campaign) return;

    // Extract configuration data from campaign
    this.agentConfigData = this.campaign.agent_config || null;
    this.conversationConfigData = this.campaign.conversation_config || null;
    this.timeSettingsData = this.campaign.time_settings_config || null;

    // Load available campaigns for workspace
    if (this.workspaceId && this.platformId) {
      // console.log('Loading available campaigns for workspace:', this.workspaceId, 'platform:', this.platformId);
      this.loadAvailableCampaigns();
    }

    this.cdr.markForCheck();
  }

  /**
   * Loads available campaigns for the current workspace and platform
   */
  private loadAvailableCampaigns(): void {
    this.isLoadingCampaigns = true;

    this.campaignService.getCampaignsByWorkspaceId(this.workspaceId)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Error loading campaigns for workspace:', error);
          return EMPTY;
        }),
        finalize(() => {
          this.isLoadingCampaigns = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe((campaigns: Campaign[]) => {
        this.availableCampaigns = campaigns.filter(campaign => campaign.id !== this.selectedConversation?.campaignId);
        this.cdr.markForCheck();
      });
  }

  // Campaign selection methods
  loadCampaignsByWorkspace(): void {
    if (!this.workspaceId || this.platformId <= 0) {
      this.campaignError = 'Workspace o plataforma no disponible para cargar campañas.';
      this.availableCampaigns = [];
      this.isLoadingCampaigns = false;
      this.cdr.markForCheck();
      return;
    }

    this.campaignError = null;
    this.isLoadingCampaigns = true;

    this.campaignService.getCampaignsByWorkspaceId(this.workspaceId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoadingCampaigns = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (campaigns: Campaign[]) => {
          this.availableCampaigns = campaigns.filter(campaign =>
            campaign.id !== this.selectedConversation?.campaignId
          );
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.campaignError = 'Error al cargar campañas. Intenta nuevamente.';
          this.availableCampaigns = [];
          this.cdr.markForCheck();
          console.error('Error loading campaigns by workspace:', error);
        }
      });
  }

  openCampaignDialog(): void {
    if (!this.canSelectCampaign) {
      return;
    }

    this.showCampaignDialog = true;
    this.selectedCampaign = null;

    // Load campaigns if not already loaded
    if (this.availableCampaigns.length === 0) {
      this.loadCampaignsByWorkspace();
    }

    this.cdr.markForCheck();
  }

  closeCampaignDialog(): void {
    this.showCampaignDialog = false;
    this.selectedCampaign = null;
    this.cdr.markForCheck();
  }

  selectCampaign(): void {
    if (!this.selectedCampaign || !this.selectedConversation || !this.selectedConversation.conversationId || !this.selectedCampaign.id) {
      console.warn('Cannot select campaign: Missing required data');
      return;
    }

    console.log('Transferring conversation to campaign:', {
      conversationId: this.selectedConversation.conversationId,
      fromCampaign: this.selectedConversation.campaignId,
      toCampaign: this.selectedCampaign.id
    });

    // Set loading state
    this.isLoadingCampaigns = true;
    this.cdr.markForCheck();


    this.customerConversationService.transferConversationCampaign(
      this.selectedConversation.conversationId,
      this.selectedCampaign.id
    ).pipe(
      takeUntil(this.destroy$),
      switchMap((firstResponse) => {
        console.log('First transfer successful:', firstResponse);

        //orchestador
        return this.customerConversationService.transferToGroup(
          this.selectedConversation!.conversationId!,
          this.selectedCampaign?.campaign_name || 'Unknown Campaign',
          this.selectedConversation?.keyPrefix
        );
      }),
      finalize(() => {
        this.isLoadingCampaigns = false;
        this.cdr.markForCheck();
        window.location.reload();
      })
    ).subscribe({
      next: (finalResponse) => {
        console.log('orchestador transfer successful:', finalResponse);

        // Update the conversation's campaign ID
        if (this.selectedConversation && this.selectedCampaign?.id) {
          this.selectedConversation.campaignId = this.selectedCampaign.id;
        }

        // Reload campaign data for the new campaign
        if (this.selectedCampaign?.id) {
          this.loadCampaignForConversation(this.selectedCampaign.id);
        }

        // Close the dialog
        this.closeCampaignDialog();

        // Show success message (you might want to add a toast service)
        console.log(`Conversación transferida exitosamente a la campaña: ${this.selectedCampaign?.campaign_name || 'Nueva campaña'}`);

        // Mark for change detection
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Campaign transfer failed:', error);

        // Handle transfer error (you might want to show an error message)
        let errorMessage = 'Error al transferir la conversación';
        if (error?.message) {
          errorMessage += `: ${error.message}`;
        }
        console.error(errorMessage);

        // You could implement a toast notification here:
        // this.toastService.showError(errorMessage);
      }
    });
  }


}
